import get from "lodash/get";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { COMPETITION_GENDER_FORMAT, GENDER_TYPE } from "../constants/personConstants";
import { getAgeRequirementIsMetFromDob } from "./ageUtils";
import { parseAgeGroups, formatAgeGroups } from "./parseAgeGroups";

function checkGenderEligibility(competitionGender, personGender) {
  if (includes([COMPETITION_GENDER_FORMAT.open, COMPETITION_GENDER_FORMAT.mixed], competitionGender)) {
    return true;
  }

  if (includes([GENDER_TYPE.prefer_not_to_answer, GENDER_TYPE.x], personGender)) {
    return true;
  }

  return competitionGender === personGender;
}

function checkAgeEligibility(competitionAgeGroups, ageCutoffAt, personDateOfBirth) {
  if (isEmpty(competitionAgeGroups)) {
    return true;
  }

  const parsedAgeGroups = formatAgeGroups(parseAgeGroups(competitionAgeGroups));

  return parsedAgeGroups.some(ageGroup =>
    getAgeRequirementIsMetFromDob(personDateOfBirth, ageCutoffAt, ageGroup[0], ageGroup[1]),
  );
}

/**
 * Returns whether a person is eligible to be registered in a competition based on gender and age requirements.
 *
 * @param {Object}   competitionData
 * @param {string[]} competitionData.ageGroups - Array of eligible age groups
 * @param {string}   competitionData.ageCutoffAt - Age cut off date in YYYY-MM-DD format
 * @param {string}   competitionData.gender - Eligible gender
 *
 * @param {Object} personData
 * @param {string?} personData.dob - Person's date of birth
 * @param {string?} personData.gender - Person's gender
 *
 *
 * @return {boolean}
 *
 */
export function isPersonEligibleForCompetition(competitionData, personData) {
  if (isEmpty(competitionData) || isEmpty(personData)) {
    throw Error("Invalid parameters provided.");
  }

  const personGender = personData.gender && typeof personData.gender === "string" ? personData.gender.trim() : personData.gender;
  const isPersonGenderProvided = !isNil(personGender) && personGender !== "";

  if (
    isNil(competitionData.gender) ||
    !includes(Object.values(COMPETITION_GENDER_FORMAT), competitionData.gender) ||
    (isPersonGenderProvided && !includes(Object.values(GENDER_TYPE), personGender))
  ) {
    throw Error("Either competition or person gender information is not provided correctly.");
  }

  if (!isArray(competitionData.ageGroups) || isNil(competitionData.ageCutoffAt)) {
    throw Error("Competition age requirement information is not provided correctly.");
  }

  return (
    checkGenderEligibility(competitionData.gender, isPersonGenderProvided ? personGender : null) &&
    checkAgeEligibility(competitionData.ageGroups, competitionData.ageCutoffAt, personData.dob)
  );
}

/**
 * Formats a competition label with its sport name, if available.
 * E.g. "Basketball — 18+ Unisex" or "18+ Unisex" if sport is not available.
 *
 * @param {Object} competition
 * @param {string} [competition.name]
 * @param {Object} [competition.SportMeet]
 * @param {Object} [competition.SportMeet.Sport]
 * @param {string} [competition.SportMeet.Sport.name]
 * @returns {string}
 */
export function formatCompetitionLabel(competition) {
  if (isNil(competition)) {
    return "";
  }
  if (typeof competition === "string") {
    return competition;
  }
  const competitionName = get(competition, "name", "");
  const sportName =
    get(competition, "SportMeet.Sport.name") ||
    get(competition, "Sport.name") ||
    get(competition, "sport.name") ||
    null;

  if (sportName && competitionName) {
    return `${sportName} \u2014 ${competitionName}`;
  }

  return competitionName || sportName || "";
}
